import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/App";
import usePageTitle from "@/hooks/usePageTitle";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import DeleteConfirmDialog from "@/components/DeleteConfirmDialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Users,
  Plus,
  Edit3,
  Trash2,
  Key,
  ToggleLeft,
  ToggleRight,
  Search,
  RefreshCw,
  Shield,
  User,
  Briefcase,
  Activity,
  Eye,
  EyeOff,
  CheckCircle2,
  XCircle,
  Clock,
  FileText,
  Settings,
  Upload,
  AlertTriangle,
  Package,
  DollarSign,
  Truck,
  TrendingUp,
  BookOpen,
  Save,
  Loader2,
  Layers,
  Copy,
  UserPlus,
  Zap,
  X,
  ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import { formatDMY } from "@/lib/dmy";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Available modules for permissions
const AVAILABLE_MODULES = [
  { id: "dashboard", label: "Dashboard", description: "View dashboard overview" },
  { id: "crm", label: "CRM Hub", description: "Client relationship management" },
  { id: "sales_profit", label: "Sales & Profit", description: "View sales and profit data" },
  { id: "products", label: "Products", description: "View product prices" },
  { id: "shipping", label: "Shipping Rates", description: "View shipping rates" },
  { id: "costing", label: "Shipment Costing", description: "View costing data" },
  { id: "ledger", label: "Ledger", description: "View/edit ledger" },
  { id: "ledger_reconciliation", label: "Ledger Reconciliation", description: "Access ledger reconciliation" },
  { id: "reports", label: "Reports", description: "Access reports dashboard" },
  { id: "notes", label: "Notepad", description: "Access notepad" },
  { id: "error_resolution", label: "Error Resolution", description: "Access error resolution panel" },
  { id: "upload_center", label: "Upload Center", description: "Access centralized upload center" },
  { id: "payment_verification", label: "Payment Verification", description: "Verify payments" },
];

// Available actions
const AVAILABLE_ACTIONS = [
  { id: "view", label: "View Data", description: "Can view data" },
  { id: "upload", label: "Upload Files", description: "Can upload files" },
  { id: "edit", label: "Edit Data", description: "Can edit records" },
  { id: "delete", label: "Delete Data", description: "Can delete records" },
  { id: "export", label: "Export Data", description: "Can export data" },
  { id: "edit_ledger", label: "Edit Ledger", description: "Can modify ledger entries" },
];

// Available fields (for data visibility)
const AVAILABLE_FIELDS = [
  { id: "profit", label: "Profit", description: "Can see profit data" },
  { id: "cost", label: "Cost", description: "Can see cost data" },
  { id: "margin", label: "Margin", description: "Can see profit margin" },
  { id: "client_details", label: "Client Details", description: "Can see client contact info" },
  { id: "financial", label: "Financial Data", description: "Can see financial summaries" },
];

// Upload Center sections that can be hidden per role
const UPLOAD_CENTER_SECTIONS = [
  { id: "shipment_costing", label: "Shipment Costing", icon: Package, description: "Order sheets and packing lists" },
  { id: "products", label: "Products", icon: DollarSign, description: "Product price list uploads" },
  { id: "shipping_rates", label: "Shipping Rates", icon: Truck, description: "Global shipping rate uploads" },
  { id: "client_pricing", label: "DP CLIENTS Price Lists", icon: Users, description: "Client-specific pricing" },
  { id: "client_shipping", label: "DP CLIENTS Shipping Rates", icon: Truck, description: "Client-specific shipping" },
  { id: "sales_profit", label: "Sales & Profit", icon: TrendingUp, description: "Sales data uploads" },
  { id: "ledger", label: "Ledger", icon: BookOpen, description: "Ledger and payment uploads" },
];

// Error Resolution tabs that can be hidden per role
const ERROR_RESOLUTION_TABS = [
  { id: "overview", label: "Overview", description: "Dashboard summary" },
  { id: "costing", label: "Costing Errors", description: "Shipment costing issues" },
  { id: "sales", label: "Sales Errors", description: "Unmatched sales records" },
  { id: "unassigned", label: "Unassigned Orders", description: "Orders without client" },
  { id: "history", label: "Resolution History", description: "Past resolutions" },
];

// Roles available for permission management
const MANAGEABLE_ROLES = [
  { id: "error_manager", label: "Error Manager", color: "amber" },
  { id: "uploader", label: "Uploader", color: "indigo" },
  { id: "partner", label: "Partner", color: "blue" },
  { id: "agent", label: "Agent", color: "purple" },
];

// Permission presets for quick configuration
const PERMISSION_PRESETS = [
  {
    id: "full_access",
    label: "Full Access",
    description: "Access to all sections and tabs",
    color: "bg-green-100 border-green-300 text-green-800",
    icon: "✓",
    upload_center: { hidden_sections: [] },
    error_resolution: { hidden_tabs: [] }
  },
  {
    id: "upload_only",
    label: "Upload Only",
    description: "Only shipment costing and product uploads",
    color: "bg-blue-100 border-blue-300 text-blue-800",
    icon: "↑",
    upload_center: { hidden_sections: ["sales_profit", "ledger", "client_pricing", "client_shipping"] },
    error_resolution: { hidden_tabs: ["sales", "history"] }
  },
  {
    id: "view_only",
    label: "View Only",
    description: "No financial or ledger access",
    color: "bg-amber-100 border-amber-300 text-amber-800",
    icon: "👁",
    upload_center: { hidden_sections: ["sales_profit", "ledger"] },
    error_resolution: { hidden_tabs: [] }
  },
  {
    id: "restricted",
    label: "Restricted",
    description: "Minimal access - basic operations only",
    color: "bg-red-100 border-red-300 text-red-800",
    icon: "⊘",
    upload_center: { hidden_sections: ["sales_profit", "ledger", "client_pricing", "client_shipping", "shipping_rates"] },
    error_resolution: { hidden_tabs: ["sales", "unassigned", "history"] }
  },
  {
    id: "custom",
    label: "Custom",
    description: "Manually configure each section",
    color: "bg-slate-100 border-slate-300 text-slate-800",
    icon: "⚙",
    upload_center: null, // null means don't change
    error_resolution: null
  }
];

export default function UserManagementPage() {
  usePageTitle("User Management");
  const { token, user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [activeTab, setActiveTab] = useState("users");
  
  // Dialog states
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [showLogsDialog, setShowLogsDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [activityLogs, setActivityLogs] = useState([]);
  const [showSidePanel, setShowSidePanel] = useState(false);
  
  // Permission Templates
  const PERMISSION_TEMPLATES = {
    full_access: {
      name: "Full Access",
      description: "All modules and actions",
      icon: "🔓",
      color: "bg-green-100 border-green-300",
      allowed_modules: ["dashboard", "sales_profit", "upload_sales", "products", "shipping_rates", "ledger", "shipment_costing", "error_resolution", "upload_center", "reports", "crm"],
      allowed_actions: ["view", "upload", "edit", "delete", "export", "edit_ledger"]
    },
    sales_team: {
      name: "Sales Team",
      description: "Sales, products, and reports access",
      icon: "📊",
      color: "bg-blue-100 border-blue-300",
      allowed_modules: ["dashboard", "sales_profit", "products", "reports", "crm"],
      allowed_actions: ["view", "export"]
    },
    upload_only: {
      name: "Upload Only",
      description: "Upload center access only",
      icon: "📤",
      color: "bg-indigo-100 border-indigo-300",
      allowed_modules: ["upload_center"],
      allowed_actions: ["view", "upload"]
    },
    view_only: {
      name: "View Only",
      description: "Read-only access to all modules",
      icon: "👁️",
      color: "bg-slate-100 border-slate-300",
      allowed_modules: ["dashboard", "sales_profit", "products", "shipping_rates", "ledger", "shipment_costing", "reports"],
      allowed_actions: ["view"]
    },
    error_handler: {
      name: "Error Handler",
      description: "Error resolution and basic view",
      icon: "🔧",
      color: "bg-amber-100 border-amber-300",
      allowed_modules: ["dashboard", "error_resolution", "upload_center"],
      allowed_actions: ["view", "edit_errors", "resolve_errors"]
    },
    accounts_team: {
      name: "Accounts Team",
      description: "Ledger and financial access",
      icon: "💰",
      color: "bg-emerald-100 border-emerald-300",
      allowed_modules: ["dashboard", "ledger", "shipment_costing", "sales_profit", "reports"],
      allowed_actions: ["view", "edit", "edit_ledger", "export"]
    }
  };

  // Delete confirmation dialog
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  // Ledger codes for linking
  const [ledgerCodes, setLedgerCodes] = useState([]);
  
  // Role Permissions state
  const [selectedPermRole, setSelectedPermRole] = useState("error_manager");
  const [rolePermissions, setRolePermissions] = useState({});
  const [loadingPermissions, setLoadingPermissions] = useState(false);
  const [savingPermissions, setSavingPermissions] = useState(false);
  const [activePreset, setActivePreset] = useState("custom"); // Track which preset is active
  
  // Form state
  const [formData, setFormData] = useState({
    username: "",
    password: "",
    role: "partner",
    client_code: "",
    display_name: "",
    email: "",
    phone: "",
    is_active: true,
    allowed_modules: [],
    allowed_actions: [],
    allowed_fields: [],
  });
  
  const [newPassword, setNewPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  // Update page title
  useEffect(() => {
    document.title = "User Management | EAC";
    return () => { document.title = "EAC"; };
  }, []);

  // Fetch users
  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      if (response.ok) {
        setUsers(data.users || []);
      } else {
        toast.error(data.detail || "Failed to fetch users");
      }
    } catch (error) {
      toast.error("Failed to fetch users");
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Fetch ledger codes for linking
  const fetchLedgerCodes = useCallback(async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/ledgers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      if (response.ok) {
        setLedgerCodes(data.ledgers || []);
      }
    } catch (error) {
      console.error("Error fetching ledger codes:", error);
    }
  }, [token]);

  // Fetch role permissions
  const fetchRolePermissions = useCallback(async () => {
    setLoadingPermissions(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/role-permissions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      if (response.ok) {
        setRolePermissions(data.permissions || {});
      }
    } catch (error) {
      console.error("Error fetching role permissions:", error);
    } finally {
      setLoadingPermissions(false);
    }
  }, [token]);

  // Get hidden sections for a role and panel
  const getHiddenSections = (role, panel) => {
    return rolePermissions[panel]?.[role]?.hidden_sections || [];
  };

  // Get hidden tabs for a role and panel
  const getHiddenTabs = (role, panel) => {
    return rolePermissions[panel]?.[role]?.hidden_tabs || [];
  };

  // Toggle section visibility for a role
  const toggleSectionVisibility = (role, panel, sectionId, itemType = "sections") => {
    setActivePreset("custom"); // Switch to custom when manually toggling
    setRolePermissions(prev => {
      const updated = { ...prev };
      if (!updated[panel]) updated[panel] = {};
      if (!updated[panel][role]) updated[panel][role] = { hidden_sections: [], hidden_tabs: [], hidden_actions: [] };
      
      const key = itemType === "sections" ? "hidden_sections" : itemType === "tabs" ? "hidden_tabs" : "hidden_actions";
      const currentList = updated[panel][role][key] || [];
      
      if (currentList.includes(sectionId)) {
        updated[panel][role][key] = currentList.filter(id => id !== sectionId);
      } else {
        updated[panel][role][key] = [...currentList, sectionId];
      }
      
      return updated;
    });
  };

  // Apply a preset to the selected role
  const applyPreset = async (presetId) => {
    const preset = PERMISSION_PRESETS.find(p => p.id === presetId);
    if (!preset || presetId === "custom") {
      setActivePreset("custom");
      return;
    }
    
    setActivePreset(presetId);
    
    // Update local state
    setRolePermissions(prev => {
      const updated = { ...prev };
      
      // Apply upload_center preset
      if (preset.upload_center) {
        if (!updated.upload_center) updated.upload_center = {};
        updated.upload_center[selectedPermRole] = {
          ...updated.upload_center[selectedPermRole],
          hidden_sections: [...preset.upload_center.hidden_sections]
        };
      }
      
      // Apply error_resolution preset
      if (preset.error_resolution) {
        if (!updated.error_resolution) updated.error_resolution = {};
        updated.error_resolution[selectedPermRole] = {
          ...updated.error_resolution[selectedPermRole],
          hidden_tabs: [...preset.error_resolution.hidden_tabs]
        };
      }
      
      return updated;
    });
    
    // Save both panels to backend
    setSavingPermissions(true);
    try {
      // Save upload_center permissions
      if (preset.upload_center) {
        await fetch(`${BACKEND_URL}/api/role-permissions`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            role: selectedPermRole,
            panel: "upload_center",
            hidden_items: preset.upload_center.hidden_sections,
            item_type: "sections"
          })
        });
      }
      
      // Save error_resolution permissions
      if (preset.error_resolution) {
        await fetch(`${BACKEND_URL}/api/role-permissions`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            role: selectedPermRole,
            panel: "error_resolution",
            hidden_items: preset.error_resolution.hidden_tabs,
            item_type: "tabs"
          })
        });
      }
      
      toast.success(`"${preset.label}" preset applied to ${selectedPermRole.replace('_', ' ')}`);
    } catch (error) {
      console.error("Error applying preset:", error);
      toast.error("Failed to apply preset");
    } finally {
      setSavingPermissions(false);
    }
  };

  // Detect which preset matches current configuration
  const detectActivePreset = (role) => {
    const uploadHidden = rolePermissions.upload_center?.[role]?.hidden_sections || [];
    const errorHidden = rolePermissions.error_resolution?.[role]?.hidden_tabs || [];
    
    for (const preset of PERMISSION_PRESETS) {
      if (preset.id === "custom") continue;
      
      const presetUpload = preset.upload_center?.hidden_sections || [];
      const presetError = preset.error_resolution?.hidden_tabs || [];
      
      const uploadMatch = presetUpload.length === uploadHidden.length && 
        presetUpload.every(s => uploadHidden.includes(s));
      const errorMatch = presetError.length === errorHidden.length && 
        presetError.every(t => errorHidden.includes(t));
      
      if (uploadMatch && errorMatch) {
        return preset.id;
      }
    }
    return "custom";
  };

  // Update active preset when role or permissions change
  useEffect(() => {
    if (!loadingPermissions) {
      setActivePreset(detectActivePreset(selectedPermRole));
    }
  }, [selectedPermRole, rolePermissions, loadingPermissions]);

  // Save permissions for a specific role
  const savePermissions = async (role, panel, itemType = "sections") => {
    setSavingPermissions(true);
    try {
      const key = itemType === "sections" ? "hidden_sections" : itemType === "tabs" ? "hidden_tabs" : "hidden_actions";
      const hiddenItems = rolePermissions[panel]?.[role]?.[key] || [];
      
      const response = await fetch(`${BACKEND_URL}/api/role-permissions`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          role,
          panel,
          hidden_items: hiddenItems,
          item_type: itemType
        })
      });
      
      if (response.ok) {
        toast.success(`${panel.replace('_', ' ')} permissions saved for ${role}`);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to save permissions");
      }
    } catch (error) {
      console.error("Error saving permissions:", error);
      toast.error("Failed to save permissions");
    } finally {
      setSavingPermissions(false);
    }
  };


  useEffect(() => {
    fetchUsers();
    fetchLedgerCodes();
  }, [fetchUsers, fetchLedgerCodes]);

  // Fetch permissions when tab changes to permissions
  useEffect(() => {
    if (activeTab === "permissions") {
      fetchRolePermissions();
    }
  }, [activeTab, fetchRolePermissions]);

  // Create user
  const handleCreateUser = async () => {
    if (!formData.username || !formData.password) {
      toast.error("Username and password are required");
      return;
    }
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/users`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });
      
      const data = await response.json();
      if (response.ok) {
        toast.success("User created successfully");
        setShowCreateDialog(false);
        resetForm();
        fetchUsers();
      } else {
        toast.error(data.detail || "Failed to create user");
      }
    } catch (error) {
      toast.error("Failed to create user");
    }
  };

  // Update user
  const handleUpdateUser = async () => {
    if (!selectedUser) return;
    
    try {
      const updateData = {
        display_name: formData.display_name,
        email: formData.email,
        phone: formData.phone,
        is_active: formData.is_active,
        client_code: formData.client_code,
        allowed_modules: formData.allowed_modules,
        allowed_actions: formData.allowed_actions,
        allowed_fields: formData.allowed_fields,
      };
      
      const response = await fetch(`${BACKEND_URL}/api/users/${selectedUser.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(updateData)
      });
      
      const data = await response.json();
      if (response.ok) {
        toast.success("User updated successfully");
        setShowEditDialog(false);
        setSelectedUser(null);
        fetchUsers();
      } else {
        toast.error(data.detail || "Failed to update user");
      }
    } catch (error) {
      toast.error("Failed to update user");
    }
  };

  // Change password
  const handleChangePassword = async () => {
    if (!selectedUser || !newPassword) {
      toast.error("New password is required");
      return;
    }
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/users/${selectedUser.id}/password`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ new_password: newPassword })
      });
      
      const data = await response.json();
      if (response.ok) {
        toast.success("Password changed successfully");
        setShowPasswordDialog(false);
        setNewPassword("");
        setSelectedUser(null);
      } else {
        toast.error(data.detail || "Failed to change password");
      }
    } catch (error) {
      toast.error("Failed to change password");
    }
  };

  // Toggle user status
  const handleToggleStatus = async (userId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/users/${userId}/toggle-status`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const data = await response.json();
      if (response.ok) {
        toast.success(data.message);
        fetchUsers();
      } else {
        toast.error(data.detail || "Failed to toggle status");
      }
    } catch (error) {
      toast.error("Failed to toggle status");
    }
  };

  // Trigger delete confirmation
  const triggerDeleteUser = (userId, username) => {
    setDeleteTarget({ id: userId, name: username });
    setShowDeleteDialog(true);
  };

  // Execute delete after password confirmation
  const executeDeleteUser = async () => {
    if (!deleteTarget) return;
    
    setDeleting(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/users/${deleteTarget.id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const data = await response.json();
      if (response.ok) {
        toast.success("User deleted successfully");
        fetchUsers();
      } else {
        toast.error(data.detail || "Failed to delete user");
      }
    } catch (error) {
      toast.error("Failed to delete user");
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  };

  // Fetch activity logs for a user
  const fetchUserLogs = async (userId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/activity-logs/user/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      if (response.ok) {
        setActivityLogs(data.logs || []);
      }
    } catch (error) {
      console.error("Error fetching logs:", error);
    }
  };

  // Open edit dialog
  const openEditDialog = (user) => {
    setSelectedUser(user);
    setFormData({
      username: user.username,
      password: "",
      role: user.role,
      client_code: user.client_code || "",
      display_name: user.display_name || "",
      email: user.email || "",
      phone: user.phone || "",
      is_active: user.is_active !== false,
      allowed_modules: user.allowed_modules || [],
      allowed_actions: user.allowed_actions || [],
      allowed_fields: user.allowed_fields || [],
    });
    setShowEditDialog(true);
  };

  // Open logs dialog
  const openLogsDialog = (user) => {
    setSelectedUser(user);
    fetchUserLogs(user.id);
    setShowLogsDialog(true);
  };

  // Open side panel with user details
  const openSidePanel = (user) => {
    setSelectedUser(user);
    setShowSidePanel(true);
  };

  // Apply permission template
  const applyTemplate = async (templateKey) => {
    if (!selectedUser) return;
    
    const template = PERMISSION_TEMPLATES[templateKey];
    try {
      const response = await fetch(`${BACKEND_URL}/api/users/${selectedUser.id}`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          allowed_modules: template.allowed_modules,
          allowed_actions: template.allowed_actions
        }),
      });
      
      if (response.ok) {
        toast.success(`Applied "${template.name}" template to ${selectedUser.username}`);
        fetchUsers();
        // Update selected user with new permissions
        setSelectedUser(prev => ({
          ...prev,
          allowed_modules: template.allowed_modules,
          allowed_actions: template.allowed_actions
        }));
      } else {
        toast.error("Failed to apply template");
      }
    } catch (error) {
      toast.error("Failed to apply template");
    }
  };

  // Quick toggle user active status
  const toggleUserStatus = async (user) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/users/${user.id}`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          is_active: !user.is_active
        }),
      });
      
      if (response.ok) {
        toast.success(`User ${user.is_active ? 'deactivated' : 'activated'} successfully`);
        fetchUsers();
        if (selectedUser?.id === user.id) {
          setSelectedUser(prev => ({ ...prev, is_active: !prev.is_active }));
        }
      } else {
        toast.error("Failed to update user status");
      }
    } catch (error) {
      toast.error("Failed to update user status");
    }
  };

  // Duplicate user (create new with same permissions)
  const duplicateUser = () => {
    if (!selectedUser) return;
    
    setFormData({
      username: "",
      password: "",
      role: selectedUser.role,
      client_code: "",
      display_name: "",
      email: "",
      phone: "",
      is_active: true,
      allowed_modules: selectedUser.allowed_modules || [],
      allowed_actions: selectedUser.allowed_actions || [],
      allowed_fields: selectedUser.allowed_fields || [],
    });
    setShowSidePanel(false);
    setShowCreateDialog(true);
    toast.info("Creating new user with same permissions");
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      username: "",
      password: "",
      role: "partner",
      client_code: "",
      display_name: "",
      email: "",
      phone: "",
      is_active: true,
      allowed_modules: [],
      allowed_actions: [],
      allowed_fields: [],
    });
  };

  // Toggle array item
  const toggleArrayItem = (array, item) => {
    if (array.includes(item)) {
      return array.filter(i => i !== item);
    }
    return [...array, item];
  };

  // Filter users
  const filteredUsers = users.filter(u => {
    const matchesSearch = !searchTerm || 
      u.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.display_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.client_code?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = roleFilter === "all" || u.role === roleFilter;
    return matchesSearch && matchesRole;
  });

  // Get role badge style
  const getRoleBadge = (role) => {
    switch (role) {
      case "super_admin":
        return "bg-red-100 text-red-700 border-red-200";
      case "partner":
        return "bg-blue-100 text-blue-700 border-blue-200";
      case "agent":
        return "bg-purple-100 text-purple-700 border-purple-200";
      case "error_manager":
        return "bg-amber-100 text-amber-700 border-amber-200";
      case "uploader":
        return "bg-indigo-100 text-indigo-700 border-indigo-200";
      default:
        return "bg-slate-100 text-slate-700 border-slate-200";
    }
  };
  
  // Get role display name
  const getRoleDisplayName = (role) => {
    switch (role) {
      case "super_admin": return "Admin";
      case "partner": return "Partner";
      case "agent": return "Agent";
      case "error_manager": return "Error Manager";
      case "uploader": return "Uploader";
      default: return role;
    }
  };

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-indigo-100 rounded-lg">
            <Users className="w-6 h-6 text-indigo-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">User Management</h1>
            <p className="text-sm text-slate-500">Manage users and role permissions</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {activeTab === "users" && (
            <>
              <Button variant="outline" size="sm" onClick={fetchUsers}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
              <Button onClick={() => { resetForm(); setShowCreateDialog(true); }}>
                <Plus className="w-4 h-4 mr-2" />
                Add User
              </Button>
            </>
          )}
          {activeTab === "permissions" && (
            <Button variant="outline" size="sm" onClick={fetchRolePermissions}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="users" className="gap-2" data-testid="tab-users">
            <Users className="w-4 h-4" />
            Users
          </TabsTrigger>
          <TabsTrigger value="permissions" className="gap-2" data-testid="tab-permissions">
            <Shield className="w-4 h-4" />
            Role Permissions
          </TabsTrigger>
        </TabsList>

        {/* Users Tab */}
        <TabsContent value="users" className="mt-6">
          {/* Filters */}
          <div className="flex items-center gap-4 bg-white p-4 rounded-lg border mb-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search by username, name, or code..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={roleFilter} onValueChange={setRoleFilter}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Filter by role" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Roles</SelectItem>
            <SelectItem value="partner">Partners</SelectItem>
            <SelectItem value="agent">Agents</SelectItem>
            <SelectItem value="error_manager">Error Managers</SelectItem>
            <SelectItem value="uploader">Uploaders</SelectItem>
            <SelectItem value="super_admin">Admins</SelectItem>
          </SelectContent>
        </Select>
        <div className="text-sm text-slate-500">
          {filteredUsers.length} user{filteredUsers.length !== 1 ? "s" : ""}
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-lg border overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12">
            <Users className="w-12 h-12 text-slate-300" />
            <p className="text-slate-500 mt-4">No users found</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="w-12">#</TableHead>
                <TableHead>Username</TableHead>
                <TableHead>Display Name</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Client Code</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Modules</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="text-center">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredUsers.map((u, idx) => (
                <TableRow key={u.id} className="hover:bg-slate-50">
                  <TableCell className="text-slate-500">{idx + 1}</TableCell>
                  <TableCell className="font-medium">{u.username}</TableCell>
                  <TableCell>{u.display_name || "-"}</TableCell>
                  <TableCell>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getRoleBadge(u.role)}`}>
                      {getRoleDisplayName(u.role)}
                    </span>
                  </TableCell>
                  <TableCell className="font-mono text-sm">{u.client_code || "-"}</TableCell>
                  <TableCell>
                    {u.is_active !== false ? (
                      <span className="flex items-center gap-1 text-green-600">
                        <CheckCircle2 className="w-4 h-4" />
                        Active
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-red-600">
                        <XCircle className="w-4 h-4" />
                        Disabled
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    <span className="text-xs text-slate-500">
                      {(u.allowed_modules || []).length} modules
                    </span>
                  </TableCell>
                  <TableCell className="text-sm text-slate-500">
                    {u.created_at ? formatDMY(u.created_at) : "-"}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center justify-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0"
                        onClick={() => openEditDialog(u)}
                        title="Edit"
                      >
                        <Edit3 className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0"
                        onClick={() => { setSelectedUser(u); setShowPasswordDialog(true); }}
                        title="Change Password"
                      >
                        <Key className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0"
                        onClick={() => handleToggleStatus(u.id)}
                        title={u.is_active !== false ? "Disable" : "Enable"}
                      >
                        {u.is_active !== false ? (
                          <ToggleRight className="w-4 h-4 text-green-600" />
                        ) : (
                          <ToggleLeft className="w-4 h-4 text-slate-400" />
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0"
                        onClick={() => openLogsDialog(u)}
                        title="View Activity"
                      >
                        <Activity className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 text-indigo-600 hover:text-indigo-700"
                        onClick={() => openSidePanel(u)}
                        title="Quick Actions"
                      >
                        <Layers className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                        onClick={() => triggerDeleteUser(u.id, u.username)}
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
        </TabsContent>

        {/* Role Permissions Tab */}
        <TabsContent value="permissions" className="mt-6">
          <div className="space-y-6">
            {/* Role Selection */}
            <Card>
              <CardHeader className="pb-4">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Settings className="w-5 h-5 text-indigo-600" />
                  Configure Role Visibility
                </CardTitle>
                <CardDescription>
                  Select a role and configure which sections are visible or hidden for that role in Upload Center and Error Resolution panels.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Role Buttons */}
                <div className="flex flex-wrap gap-2">
                  {MANAGEABLE_ROLES.map((role) => (
                    <Button
                      key={role.id}
                      variant={selectedPermRole === role.id ? "default" : "outline"}
                      size="sm"
                      onClick={() => setSelectedPermRole(role.id)}
                      data-testid={`perm-role-${role.id}`}
                    >
                      {role.label}
                    </Button>
                  ))}
                </div>
                
                {/* Permission Presets */}
                <div className="pt-3 border-t">
                  <p className="text-sm font-medium text-slate-700 mb-2">Quick Presets for {selectedPermRole.replace('_', ' ')}</p>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                    {PERMISSION_PRESETS.map((preset) => (
                      <button
                        key={preset.id}
                        onClick={() => applyPreset(preset.id)}
                        disabled={savingPermissions}
                        data-testid={`preset-${preset.id}`}
                        className={`p-3 rounded-lg border-2 transition-all text-left ${
                          activePreset === preset.id 
                            ? `${preset.color} border-current ring-2 ring-offset-1` 
                            : "bg-white border-slate-200 hover:border-slate-300"
                        } ${savingPermissions ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-lg">{preset.icon}</span>
                          <span className="font-medium text-sm">{preset.label}</span>
                        </div>
                        <p className="text-xs text-slate-500 line-clamp-2">{preset.description}</p>
                      </button>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            {loadingPermissions ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
                <span className="ml-2 text-slate-500">Loading permissions...</span>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Upload Center Sections */}
                <Card>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Upload className="w-5 h-5 text-blue-600" />
                        <CardTitle className="text-base">Upload Center Sections</CardTitle>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => savePermissions(selectedPermRole, "upload_center", "sections")}
                        disabled={savingPermissions}
                        className="gap-1"
                        data-testid="save-upload-center-perms"
                      >
                        {savingPermissions ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                        Save
                      </Button>
                    </div>
                    <CardDescription className="text-xs">
                      Toggle sections to hide them for <Badge variant="outline" className="ml-1">{selectedPermRole.replace('_', ' ')}</Badge>
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {UPLOAD_CENTER_SECTIONS.map((section) => {
                      const isHidden = getHiddenSections(selectedPermRole, "upload_center").includes(section.id);
                      const IconComponent = section.icon;
                      return (
                        <div
                          key={section.id}
                          data-testid={`upload-section-${section.id}`}
                          className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${
                            isHidden ? "bg-red-50 border-red-200" : "bg-slate-50 border-slate-200"
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <IconComponent className={`w-4 h-4 ${isHidden ? "text-red-400" : "text-slate-500"}`} />
                            <div>
                              <p className={`text-sm font-medium ${isHidden ? "text-red-700" : "text-slate-700"}`}>
                                {section.label}
                              </p>
                              <p className="text-xs text-slate-500">{section.description}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {isHidden && (
                              <Badge variant="destructive" className="text-xs">Hidden</Badge>
                            )}
                            <Switch
                              checked={!isHidden}
                              onCheckedChange={() => toggleSectionVisibility(selectedPermRole, "upload_center", section.id, "sections")}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </CardContent>
                </Card>

                {/* Error Resolution Tabs */}
                <Card>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <AlertTriangle className="w-5 h-5 text-amber-500" />
                        <CardTitle className="text-base">Error Resolution Tabs</CardTitle>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => savePermissions(selectedPermRole, "error_resolution", "tabs")}
                        disabled={savingPermissions}
                        className="gap-1"
                        data-testid="save-error-resolution-perms"
                      >
                        {savingPermissions ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                        Save
                      </Button>
                    </div>
                    <CardDescription className="text-xs">
                      Toggle tabs to hide them for <Badge variant="outline" className="ml-1">{selectedPermRole.replace('_', ' ')}</Badge>
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {ERROR_RESOLUTION_TABS.map((tab) => {
                      const isHidden = getHiddenTabs(selectedPermRole, "error_resolution").includes(tab.id);
                      return (
                        <div
                          key={tab.id}
                          data-testid={`error-tab-${tab.id}`}
                          className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${
                            isHidden ? "bg-red-50 border-red-200" : "bg-slate-50 border-slate-200"
                          }`}
                        >
                          <div>
                            <p className={`text-sm font-medium ${isHidden ? "text-red-700" : "text-slate-700"}`}>
                              {tab.label}
                            </p>
                            <p className="text-xs text-slate-500">{tab.description}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            {isHidden && (
                              <Badge variant="destructive" className="text-xs">Hidden</Badge>
                            )}
                            <Switch
                              checked={!isHidden}
                              onCheckedChange={() => toggleSectionVisibility(selectedPermRole, "error_resolution", tab.id, "tabs")}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Legend */}
            <div className="flex items-center gap-6 p-4 bg-slate-50 rounded-lg text-sm text-slate-600">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded bg-slate-200"></div>
                <span>Visible (Switch ON)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded bg-red-200"></div>
                <span>Hidden (Switch OFF)</span>
              </div>
              <div className="ml-auto text-xs text-slate-500">
                Changes take effect immediately after saving
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        title="Delete User"
        description="This will permanently delete the user account and all associated data."
        itemName={deleteTarget?.name}
        onConfirm={executeDeleteUser}
        loading={deleting}
      />

      {/* Create User Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5" />
              Create New User
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Username *</Label>
                <Input
                  value={formData.username}
                  onChange={(e) => setFormData(f => ({ ...f, username: e.target.value }))}
                  placeholder="Enter username"
                />
              </div>
              <div className="space-y-2">
                <Label>Password *</Label>
                <div className="relative">
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={(e) => setFormData(f => ({ ...f, password: e.target.value }))}
                    placeholder="Enter password"
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4 text-slate-400" /> : <Eye className="w-4 h-4 text-slate-400" />}
                  </button>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Role *</Label>
                <Select value={formData.role} onValueChange={(v) => setFormData(f => ({ ...f, role: v }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="partner">Partner</SelectItem>
                    <SelectItem value="agent">Agent</SelectItem>
                    <SelectItem value="error_manager">Error Manager</SelectItem>
                    <SelectItem value="uploader">Uploader</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Link to Client Code</Label>
                <Select 
                  value={formData.client_code || "none"} 
                  onValueChange={(v) => setFormData(f => ({ ...f, client_code: v === "none" ? "" : v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select code..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {ledgerCodes
                      .filter(l => formData.role === "partner" ? l.role === "Partner" : l.role === "Agent")
                      .map(l => (
                        <SelectItem key={l.client_code} value={l.client_code}>
                          {l.client_code} - {l.name || ""}
                        </SelectItem>
                      ))
                    }
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Display Name</Label>
                <Input
                  value={formData.display_name}
                  onChange={(e) => setFormData(f => ({ ...f, display_name: e.target.value }))}
                  placeholder="Display name"
                />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(f => ({ ...f, email: e.target.value }))}
                  placeholder="Email address"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Phone</Label>
              <Input
                value={formData.phone}
                onChange={(e) => setFormData(f => ({ ...f, phone: e.target.value }))}
                placeholder="Phone number"
              />
            </div>

            {/* Modules Access */}
            <div className="space-y-3">
              <Label className="text-base font-semibold">Allowed Modules</Label>
              <div className="grid grid-cols-2 gap-2">
                {AVAILABLE_MODULES.map(m => (
                  <div key={m.id} className="flex items-center space-x-2 p-2 border rounded hover:bg-slate-50">
                    <Checkbox
                      id={`module-${m.id}`}
                      checked={formData.allowed_modules.includes(m.id)}
                      onCheckedChange={() => setFormData(f => ({
                        ...f,
                        allowed_modules: toggleArrayItem(f.allowed_modules, m.id)
                      }))}
                    />
                    <label htmlFor={`module-${m.id}`} className="text-sm cursor-pointer flex-1">
                      <span className="font-medium">{m.label}</span>
                      <p className="text-xs text-slate-500">{m.description}</p>
                    </label>
                  </div>
                ))}
              </div>
            </div>

            {/* Actions */}
            <div className="space-y-3">
              <Label className="text-base font-semibold">Allowed Actions</Label>
              <div className="grid grid-cols-3 gap-2">
                {AVAILABLE_ACTIONS.map(a => (
                  <div key={a.id} className="flex items-center space-x-2 p-2 border rounded hover:bg-slate-50">
                    <Checkbox
                      id={`action-${a.id}`}
                      checked={formData.allowed_actions.includes(a.id)}
                      onCheckedChange={() => setFormData(f => ({
                        ...f,
                        allowed_actions: toggleArrayItem(f.allowed_actions, a.id)
                      }))}
                    />
                    <label htmlFor={`action-${a.id}`} className="text-sm cursor-pointer">{a.label}</label>
                  </div>
                ))}
              </div>
            </div>

            {/* Fields */}
            <div className="space-y-3">
              <Label className="text-base font-semibold">Visible Data Fields</Label>
              <div className="grid grid-cols-3 gap-2">
                {AVAILABLE_FIELDS.map(f => (
                  <div key={f.id} className="flex items-center space-x-2 p-2 border rounded hover:bg-slate-50">
                    <Checkbox
                      id={`field-${f.id}`}
                      checked={formData.allowed_fields.includes(f.id)}
                      onCheckedChange={() => setFormData(fd => ({
                        ...fd,
                        allowed_fields: toggleArrayItem(fd.allowed_fields, f.id)
                      }))}
                    />
                    <label htmlFor={`field-${f.id}`} className="text-sm cursor-pointer">{f.label}</label>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="is_active"
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData(f => ({ ...f, is_active: checked }))}
              />
              <Label htmlFor="is_active">Account is active</Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateUser}>Create User</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit3 className="w-5 h-5" />
              Edit User: {selectedUser?.username}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Display Name</Label>
                <Input
                  value={formData.display_name}
                  onChange={(e) => setFormData(f => ({ ...f, display_name: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(f => ({ ...f, email: e.target.value }))}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData(f => ({ ...f, phone: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Link to Client Code</Label>
                <Select 
                  value={formData.client_code || "none"} 
                  onValueChange={(v) => setFormData(f => ({ ...f, client_code: v === "none" ? "" : v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select code..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {ledgerCodes.map(l => (
                      <SelectItem key={l.client_code} value={l.client_code}>
                        {l.client_code} - {l.name || ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Modules Access */}
            <div className="space-y-3">
              <Label className="text-base font-semibold">Allowed Modules</Label>
              <div className="grid grid-cols-2 gap-2">
                {AVAILABLE_MODULES.map(m => (
                  <div key={m.id} className="flex items-center space-x-2 p-2 border rounded hover:bg-slate-50">
                    <Checkbox
                      id={`edit-module-${m.id}`}
                      checked={formData.allowed_modules.includes(m.id)}
                      onCheckedChange={() => setFormData(f => ({
                        ...f,
                        allowed_modules: toggleArrayItem(f.allowed_modules, m.id)
                      }))}
                    />
                    <label htmlFor={`edit-module-${m.id}`} className="text-sm cursor-pointer flex-1">
                      <span className="font-medium">{m.label}</span>
                      <p className="text-xs text-slate-500">{m.description}</p>
                    </label>
                  </div>
                ))}
              </div>
            </div>

            {/* Actions */}
            <div className="space-y-3">
              <Label className="text-base font-semibold">Allowed Actions</Label>
              <div className="grid grid-cols-3 gap-2">
                {AVAILABLE_ACTIONS.map(a => (
                  <div key={a.id} className="flex items-center space-x-2 p-2 border rounded hover:bg-slate-50">
                    <Checkbox
                      id={`edit-action-${a.id}`}
                      checked={formData.allowed_actions.includes(a.id)}
                      onCheckedChange={() => setFormData(f => ({
                        ...f,
                        allowed_actions: toggleArrayItem(f.allowed_actions, a.id)
                      }))}
                    />
                    <label htmlFor={`edit-action-${a.id}`} className="text-sm cursor-pointer">{a.label}</label>
                  </div>
                ))}
              </div>
            </div>

            {/* Fields */}
            <div className="space-y-3">
              <Label className="text-base font-semibold">Visible Data Fields</Label>
              <div className="grid grid-cols-3 gap-2">
                {AVAILABLE_FIELDS.map(f => (
                  <div key={f.id} className="flex items-center space-x-2 p-2 border rounded hover:bg-slate-50">
                    <Checkbox
                      id={`edit-field-${f.id}`}
                      checked={formData.allowed_fields.includes(f.id)}
                      onCheckedChange={() => setFormData(fd => ({
                        ...fd,
                        allowed_fields: toggleArrayItem(fd.allowed_fields, f.id)
                      }))}
                    />
                    <label htmlFor={`edit-field-${f.id}`} className="text-sm cursor-pointer">{f.label}</label>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="edit_is_active"
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData(f => ({ ...f, is_active: checked }))}
              />
              <Label htmlFor="edit_is_active">Account is active</Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
            <Button onClick={handleUpdateUser}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Password Dialog */}
      <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="w-5 h-5" />
              Change Password: {selectedUser?.username}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>New Password</Label>
              <div className="relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="w-4 h-4 text-slate-400" /> : <Eye className="w-4 h-4 text-slate-400" />}
                </button>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowPasswordDialog(false); setNewPassword(""); }}>Cancel</Button>
            <Button onClick={handleChangePassword}>Change Password</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Activity Logs Dialog */}
      <Dialog open={showLogsDialog} onOpenChange={setShowLogsDialog}>
        <DialogContent className="max-w-3xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Activity className="w-5 h-5" />
              Activity Logs: {selectedUser?.username}
            </DialogTitle>
          </DialogHeader>
          
          <div className="max-h-[60vh] overflow-y-auto">
            {activityLogs.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12">
                <FileText className="w-12 h-12 text-slate-300" />
                <p className="text-slate-500 mt-4">No activity logs found</p>
              </div>
            ) : (
              <div className="space-y-2">
                {activityLogs.map((log, idx) => (
                  <div key={log.id || idx} className="p-3 border rounded-lg hover:bg-slate-50">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-sm">{log.action}</span>
                      <span className="text-xs text-slate-500">
                        {new Date(log.timestamp).toLocaleString()}
                      </span>
                    </div>
                    {log.details && (
                      <p className="text-sm text-slate-600 mt-1">
                        {typeof log.details === "object"
                          ? (() => {
                              try {
                                const parts = [];
                                for (const [k, v] of Object.entries(log.details)) {
                                  if (v === null || v === undefined) continue;
                                  const s = typeof v === "object" ? JSON.stringify(v) : String(v);
                                  if (s.length <= 80) parts.push(`${k}=${s}`);
                                }
                                return parts.length ? parts.join(" · ") : JSON.stringify(log.details);
                              } catch { return "[unserialisable]"; }
                            })()
                          : log.details}
                      </p>
                    )}
                    <div className="flex items-center gap-4 mt-2 text-xs text-slate-400">
                      <span>Module: {log.module || "-"}</span>
                      <span>IP: {log.ip_address || "-"}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLogsDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Side Panel for Quick Actions */}
      {showSidePanel && selectedUser && (
        <div className="fixed inset-0 z-50 flex justify-end">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/30 transition-opacity"
            onClick={() => setShowSidePanel(false)}
          />
          
          {/* Panel */}
          <div className="relative w-96 bg-white shadow-xl h-full overflow-y-auto animate-slide-in-right">
            {/* Header */}
            <div className="sticky top-0 bg-white border-b p-4 z-10">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-bold">
                    {selectedUser.username?.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <h3 className="font-semibold">{selectedUser.username}</h3>
                    <p className="text-xs text-slate-500">{getRoleDisplayName(selectedUser.role)}</p>
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setShowSidePanel(false)}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="p-4 space-y-4">
              <div>
                <h4 className="text-sm font-medium text-slate-600 mb-2 flex items-center gap-2">
                  <Zap className="w-4 h-4" /> Quick Actions
                </h4>
                <div className="space-y-2">
                  <Button 
                    variant="outline" 
                    className="w-full justify-start" 
                    onClick={() => { openEditDialog(selectedUser); setShowSidePanel(false); }}
                  >
                    <Edit3 className="w-4 h-4 mr-2" /> Edit User Details
                  </Button>
                  <Button 
                    variant="outline" 
                    className="w-full justify-start"
                    onClick={() => { setShowPasswordDialog(true); setShowSidePanel(false); }}
                  >
                    <Key className="w-4 h-4 mr-2" /> Change Password
                  </Button>
                  <Button 
                    variant="outline" 
                    className="w-full justify-start"
                    onClick={duplicateUser}
                  >
                    <Copy className="w-4 h-4 mr-2" /> Duplicate User
                  </Button>
                  <Button 
                    variant={selectedUser.is_active !== false ? "outline" : "default"}
                    className={`w-full justify-start ${selectedUser.is_active !== false ? 'text-amber-600 hover:text-amber-700' : 'bg-green-600 hover:bg-green-700'}`}
                    onClick={() => toggleUserStatus(selectedUser)}
                  >
                    {selectedUser.is_active !== false ? (
                      <>
                        <ToggleLeft className="w-4 h-4 mr-2" /> Deactivate User
                      </>
                    ) : (
                      <>
                        <ToggleRight className="w-4 h-4 mr-2" /> Activate User
                      </>
                    )}
                  </Button>
                </div>
              </div>

              {/* Current Permissions Summary */}
              <div>
                <h4 className="text-sm font-medium text-slate-600 mb-2 flex items-center gap-2">
                  <Shield className="w-4 h-4" /> Current Permissions
                </h4>
                <div className="bg-slate-50 rounded-lg p-3 space-y-2">
                  <div>
                    <span className="text-xs text-slate-500">Modules ({(selectedUser.allowed_modules || []).length})</span>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {(selectedUser.allowed_modules || []).slice(0, 5).map(m => (
                        <Badge key={m} variant="outline" className="text-xs">{m}</Badge>
                      ))}
                      {(selectedUser.allowed_modules || []).length > 5 && (
                        <Badge variant="outline" className="text-xs">+{(selectedUser.allowed_modules || []).length - 5}</Badge>
                      )}
                    </div>
                  </div>
                  <div>
                    <span className="text-xs text-slate-500">Actions ({(selectedUser.allowed_actions || []).length})</span>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {(selectedUser.allowed_actions || []).map(a => (
                        <Badge key={a} variant="secondary" className="text-xs">{a}</Badge>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Permission Templates */}
              <div>
                <h4 className="text-sm font-medium text-slate-600 mb-2 flex items-center gap-2">
                  <Layers className="w-4 h-4" /> Apply Permission Template
                </h4>
                <div className="space-y-2">
                  {Object.entries(PERMISSION_TEMPLATES).map(([key, template]) => (
                    <button
                      key={key}
                      onClick={() => applyTemplate(key)}
                      className={`w-full p-3 rounded-lg border ${template.color} text-left hover:shadow-md transition-shadow`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="text-lg">{template.icon}</span>
                          <div>
                            <p className="font-medium text-sm">{template.name}</p>
                            <p className="text-xs text-slate-500">{template.description}</p>
                          </div>
                        </div>
                        <ChevronRight className="w-4 h-4 text-slate-400" />
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* User Info */}
              <div>
                <h4 className="text-sm font-medium text-slate-600 mb-2 flex items-center gap-2">
                  <User className="w-4 h-4" /> User Info
                </h4>
                <div className="bg-slate-50 rounded-lg p-3 space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Client Code</span>
                    <span className="font-medium">{selectedUser.client_code || "-"}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Display Name</span>
                    <span className="font-medium">{selectedUser.display_name || "-"}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Email</span>
                    <span className="font-medium">{selectedUser.email || "-"}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Created</span>
                    <span className="font-medium">
                      {selectedUser.created_at ? formatDMY(selectedUser.created_at) : "-"}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Status</span>
                    <Badge className={selectedUser.is_active !== false ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}>
                      {selectedUser.is_active !== false ? "Active" : "Inactive"}
                    </Badge>
                  </div>
                </div>
              </div>

              {/* Danger Zone */}
              <div className="pt-4 border-t">
                <h4 className="text-sm font-medium text-red-600 mb-2 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" /> Danger Zone
                </h4>
                <Button 
                  variant="outline"
                  className="w-full justify-start text-red-600 border-red-200 hover:bg-red-50"
                  onClick={() => { triggerDeleteUser(selectedUser.id, selectedUser.username); setShowSidePanel(false); }}
                >
                  <Trash2 className="w-4 h-4 mr-2" /> Delete User
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
